// useQuiz.ts
import { useState, ReactNode } from "react";

export interface QuizOption {
  id: string;
  label: ReactNode;
}

export interface  QuizQuestion {
  id: string;
  prompt: ReactNode;
  options: QuizOption[];
  correctOptionId: string;
  // Optional rationale shown on the results screen for wrong answers.
  explanation?: ReactNode;
}

export type QuizPhase = "intro" | "questions" | "results";

export interface UseQuizOptions {
  // Minimum number of correct answers to count as passed. Defaults to
  // ceil(questions.length * 0.8) — i.e. 4 out of 5.
  passThreshold?: number;
}

export const useQuiz = (
  questions: QuizQuestion[],
  options?: UseQuizOptions,
) => {
  const passThreshold =
    options?.passThreshold ?? Math.ceil(questions.length * 0.8);

  const [phase, setPhase] = useState<QuizPhase>("intro");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  // Maps questionId -> selected optionId. Tracked per-question so Previous
  // preserves the user's prior selection.
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const start = () => {
    setAnswers({});
    setCurrentQuestionIndex(0);
    setPhase("questions");
  };

  const selectAnswer = (optionId: string) => {
    const q = questions[currentQuestionIndex];
    if (!q) return;
    // Lock the answer once committed — inline feedback (green/red) reveals
    // the correct answer, so allowing a second pick would let users fish
    // for the right one. Resets only happen via retry().
    if (answers[q.id] !== undefined) return;
    setAnswers((prev) => ({ ...prev, [q.id]: optionId }));
  };

  const next = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const previous = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const submit = () => {
    setPhase("results");
  };

  const retry = () => {
    setAnswers({});
    setCurrentQuestionIndex(0);
    setPhase("questions");
  };

  // Reset to the pre-start state. Call when the dialog closes so a re-open
  // begins fresh at the intro phase.
  const reset = () => {
    setAnswers({});
    setCurrentQuestionIndex(0);
    setPhase("intro");
  };

  const currentQuestion = questions[currentQuestionIndex] ?? null;
  const selectedOptionId = currentQuestion
    ? answers[currentQuestion.id]
    : undefined;

  const score = questions.reduce(
    (acc, q) => acc + (answers[q.id] === q.correctOptionId ? 1 : 0),
    0,
  );

  const failedQuestions = questions.filter(
    (q) => answers[q.id] !== q.correctOptionId,
  );

  const passed = score >= passThreshold;
  const isLastQuestion = currentQuestionIndex === questions.length - 1;

  return {
    phase,
    currentQuestion,
    currentQuestionIndex,
    totalQuestions: questions.length,
    selectedOptionId,
    answers,
    score,
    failedQuestions,
    passed,
    passThreshold,
    isLastQuestion,
    start,
    selectAnswer,
    next,
    previous,
    submit,
    retry,
    reset,
  };
};
